import { gql, useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import Select from 'react-select'
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Button, Alert } from 'reactstrap'
import { useApolloClient } from '@apollo/client'
import "./RenderAddCustomColumn.css"
import { CUSTOM_FK_CONFIG } from "./ForeignKeyComponents"
import { STANDARD_COLUMN_TYPE_META, FK_MODEL_META, formatContentTypeLabel } from "../columnTypeMeta"

const ColumnTypesQuery = gql`query ColumnTypesQuery {
    __type(name:"TaskRabbitColumnColumnTypeChoices") {
        enumValues {
            name
            description
        }
    }
}`
const ContentTypesQuery = gql`query ContentTypesQuery($modelsIn: [String!]!) {
    allContentTypes(modelsIn: $modelsIn) {
        edges {
            node {
                id
                appLabel
                model
            }
        }
    }
}`
const BoardColumnsQuery = gql`query BoardColumnsQuery($boardId: ID!) {
    allColumns(boardId: $boardId, isArchived: false) {
        edges {
            node {
                id
                title
                columnType
                contentType {
                    id
                    model
                }
            }
        }
    }
}`
const MirrorFieldOptionsQuery = gql`query MirrorFieldOptionsQuery($parentColumnId: ID!) {
    mirrorFieldOptions(parentColumnId: $parentColumnId) {
        name
        label
    }
}`

const CreateBoardColumnMutation = gql`mutation CreateBoardColumnMutation($input: CreateBoardColumnMutationInput!){
    createBoardColumn(input: $input){
        ok
        errors{
            field
            messages
        }
    }
}`

const ALLOWED_CONTENT_TYPES = Object.keys(CUSTOM_FK_CONFIG)
const CUSTOM_MIRROR_OPTION = "__CUSTOM__"

const formatTypeLabel = ({ icon, label, helper }) => (
    <div className="custom-column-type-option">
        <span className="custom-column-type-icon"><i className={`fa ${icon}`} aria-hidden="true" /></span>
        <span className="custom-column-type-copy">
            <span className="custom-column-type-title">{label}</span>
            {helper && <span className="custom-column-type-helper">{helper}</span>}
        </span>
    </div>
)

const selectMenuProps = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed",
    styles: {
        menuPortal: (base) => ({ ...base, zIndex: 2000 }),
    },
}

const RenderAddCustomColumn = ({ refetchQuery }) => {
    const apolloClient = useApolloClient()
    const boardId = useParams().boardId
    const [openModal, setOpenModal] = useState(false)
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [formData, setFormData] = useState({
        boardId: boardId,
        title: '',
        typeSelection: 'STRING',
        columnType: 'STRING',
        isRequired: false,
        contentType: null,
        parentColumnId: null,
        mirrorField: "",
        mirrorPath: "",
        customMirrorPath: "",
        allowedOptions: [],
        stateTransitions: null,
    })
    const { data: columnTypesData } = useQuery(ColumnTypesQuery)
    const { data: contentTypesData } = useQuery(ContentTypesQuery, {
        variables: {
            modelsIn: ALLOWED_CONTENT_TYPES
        }
    })
    const { data: boardColumnsData } = useQuery(BoardColumnsQuery, {
        variables: { boardId },
        skip: !boardId,
    })
    const { data: mirrorFieldOptionsData } = useQuery(MirrorFieldOptionsQuery, {
        variables: { parentColumnId: formData.parentColumnId },
        skip: !formData.parentColumnId || formData.columnType !== "MIRROR",
    })

    useEffect(() => {
        if (!openModal) {
            setFormData({
                boardId: boardId,
                title: '',
                typeSelection: 'STRING',
                columnType: 'STRING',
                isRequired: false,
                contentType: null,
                parentColumnId: null,
                mirrorField: "",
                mirrorPath: "",
                customMirrorPath: "",
                allowedOptions: [],
                stateTransitions: null,
            })
            setError("")
        }
    }, [openModal, boardId])

    const handleChange = (e) => {
        if (e.target.name === "isRequired") {
            setFormData({ ...formData, [e.target.name]: e.target.checked })
        } else {
            setFormData({ ...formData, [e.target.name]: e.target.value })
        }
    }
    const handleSelectChange = (name, value) => {
        if (name === "typeSelection") {
            if (value.startsWith("FK_OBJECT:")) {
                const selectedContentTypeId = value.split(":")[1]
                setFormData({
                    ...formData,
                    typeSelection: value,
                    columnType: "FK_OBJECT",
                    contentType: selectedContentTypeId,
                    parentColumnId: null,
                    mirrorField: "",
                    mirrorPath: "",
                    customMirrorPath: "",
                    allowedOptions: [],
                })
            } else if (value === "MIRROR") {
                setFormData({
                    ...formData,
                    typeSelection: value,
                    columnType: "MIRROR",
                    contentType: null,
                    parentColumnId: null,
                    mirrorField: "",
                    mirrorPath: "",
                    customMirrorPath: "",
                    allowedOptions: [],
                })
            } else if (value === "ENUM") {
                setFormData({
                    ...formData,
                    typeSelection: value,
                    columnType: "ENUM",
                    contentType: null,
                    parentColumnId: null,
                    mirrorField: "",
                    mirrorPath: "",
                    customMirrorPath: "",
                })
            } else {
                setFormData({
                    ...formData,
                    typeSelection: value,
                    columnType: value,
                    contentType: null,
                    parentColumnId: null,
                    mirrorField: "",
                    mirrorPath: "",
                    customMirrorPath: "",
                    allowedOptions: value === "ENUM" ? formData.allowedOptions : [],
                })
            }
        } else if (name === "parentColumnId") {
            setFormData({ ...formData, parentColumnId: value, mirrorField: "", mirrorPath: "", customMirrorPath: "" })
        } else if (name === "mirrorField") {
            const selectedParentColumn = (boardColumnsData?.allColumns?.edges || []).find((column) => column.node.id === formData.parentColumnId)?.node
            const modelName = selectedParentColumn?.contentType?.model || ""
            setFormData({
                ...formData,
                mirrorField: value,
                mirrorPath: modelName && value && value !== CUSTOM_MIRROR_OPTION ? `${modelName}.${value}` : "",
                customMirrorPath: value === CUSTOM_MIRROR_OPTION ? formData.customMirrorPath : "",
            })
        } else if (name === "customMirrorPath") {
            const selectedParentColumn = (boardColumnsData?.allColumns?.edges || []).find((column) => column.node.id === formData.parentColumnId)?.node
            const modelName = selectedParentColumn?.contentType?.model || ""
            const trimmedValue = value.trim()
            setFormData({
                ...formData,
                customMirrorPath: value,
                mirrorPath: modelName && trimmedValue ? `${modelName}.${trimmedValue}` : "",
            })
        } else {
            setFormData({ ...formData, [name]: value })
        }
    }

    const handleAddEnumOption = () => {
        setFormData({ ...formData, allowedOptions: [...(formData.allowedOptions || []), ''] })
    }

    const handleEnumOptionChange = (index, value) => {
        const updated = [...(formData.allowedOptions || [])]
        updated[index] = value
        setFormData({ ...formData, allowedOptions: updated })
    }

    const handleRemoveEnumOption = (index) => {
        const updated = (formData.allowedOptions || []).filter((_, i) => i !== index)
        setFormData({ ...formData, allowedOptions: updated })
    }
    const handleSubmit = (e) => {
        e.preventDefault()
        if (loading) return
        setLoading(true)
        try {
            const { title, columnType, allowedOptions, stateTransitions } = formData
            const baseInput = { ...formData }
            delete baseInput.title
            delete baseInput.typeSelection
            delete baseInput.columnType
            delete baseInput.allowedOptions
            delete baseInput.stateTransitions
            delete baseInput.parentColumnId
            delete baseInput.mirrorField
            delete baseInput.customMirrorPath
            if (!title) {
                setError("Title is required")
                setLoading(false)
                return
            } else if (!columnType) {
                setError("Column type is required")
                setLoading(false)
                return
            } else if (columnType === "MIRROR" && !formData.parentColumnId) {
                setError("Parent FK column is required")
                setLoading(false)
                return
            } else if (columnType === "MIRROR" && !formData.mirrorField) {
                setError("Mirror field is required")
                setLoading(false)
                return
            } else if (columnType === "MIRROR" && formData.mirrorField === CUSTOM_MIRROR_OPTION && !formData.customMirrorPath.trim()) {
                setError("Custom mirror path is required")
                setLoading(false)
                return
            }
            const input = {
                title,
                columnType,
                ...baseInput,
                ...(columnType === "MIRROR" ? { mirrorPath: formData.mirrorPath } : {}),
                ...(columnType === "ENUM" ? { allowedOptions, stateTransitions } : {}),
            }
            apolloClient.mutate({
                mutation: CreateBoardColumnMutation,
                variables: { input },
            }).then((result) => {
                if (result?.data?.createBoardColumn?.ok) {
                    setError("")
                    refetchQuery()
                    setOpenModal(false)
                } else if (result?.data?.createBoardColumn?.errors) {
                    setError(result.data.createBoardColumn.errors[0].messages[0])
                }
            }).catch((error) => {
                setError("An error occurred, could not complete request.")
            }).finally(() => {
                setLoading(false)
            })
        } catch (error) {
            setError("An error occurred, could not complete request.")
            setLoading(false)
        }
    }

    const standardTypeOptions = (columnTypesData?.__type?.enumValues || [])
        .filter((columnType) => !["FK_OBJECT"].includes(columnType.name))
        .map((columnType) => ({
            value: columnType.name,
            label: STANDARD_COLUMN_TYPE_META[columnType.name]?.label || columnType.description,
            icon: STANDARD_COLUMN_TYPE_META[columnType.name]?.icon || "fa-columns",
            helper: columnType.name === "MIRROR" ? "Derived from an FK column" : null,
        }))

    const contentTypeOptions = (contentTypesData?.allContentTypes?.edges || []).map(({ node }) => ({
        value: `FK_OBJECT:${node.id}`,
        label: formatContentTypeLabel(node.model),
        icon: FK_MODEL_META[node.model]?.icon || "fa-cube",
        helper: "Reference an existing record",
    }))

    const typeOptions = [...standardTypeOptions, ...contentTypeOptions]
    const selectedTypeOption = typeOptions.find((option) => option.value === formData.typeSelection) || null
    const selectedParentColumn = (boardColumnsData?.allColumns?.edges || []).find((column) => column.node.id === formData.parentColumnId)?.node
    const mirrorFieldOptions = [
        ...(mirrorFieldOptionsData?.mirrorFieldOptions || []).map((option) => ({ value: option.name, label: option.label })),
        { value: CUSTOM_MIRROR_OPTION, label: "Custom path" },
    ]
    const mirrorFieldLabel = formData.mirrorField === CUSTOM_MIRROR_OPTION
        ? "Custom path"
        : (mirrorFieldOptionsData?.mirrorFieldOptions || []).find((option) => option.name === formData.mirrorField)?.label || formData.mirrorField
    return (
        <>
            <i id="addCustomColumn" className="fa fa-plus-square-o add-custom-column-icon" aria-hidden="true" onClick={() => setOpenModal(true)} />
            <Modal isOpen={openModal} toggle={() => setOpenModal(false)} className="AddCustomColumnModal" centered>
                <ModalHeader>
                    <span className="add-custom-column-modal-title"><i className="fa fa-columns" aria-hidden="true" /> Add Custom Column</span>
                </ModalHeader>
                <ModalBody>
                    {error && <Alert color="danger">{error}</Alert>}
                    <Form onSubmit={handleSubmit} id="addCustomColumnForm" className="addCustomColumnForm">
                        <div className="add-custom-column-intro">
                            Create a field for your board. You can add basic fields, link records like cars or incidents, or create a mirror column from an existing linked record.
                        </div>
                        <FormGroup className="add-custom-column-form-group">
                            <Label for="title">Column Name</Label>
                            <Input type="text" name="title" id="title" value={formData.title} onChange={handleChange} />
                        </FormGroup>
                        <FormGroup className="add-custom-column-form-group">
                            <Label for="columnType">Type</Label>
                            <Select
                                options={typeOptions}
                                name="typeSelection"
                                id="columnType"
                                value={selectedTypeOption}
                                onChange={(value) => handleSelectChange("typeSelection", value.value)}
                                formatOptionLabel={formatTypeLabel}
                                classNamePrefix="custom-column-type-select"
                                {...selectMenuProps}
                            />
                        </FormGroup>
                        {"MIRROR" === formData.columnType && <>
                            <Alert color="info" className="py-2">
                                Mirror columns are read-only. Pick a common field from the list, or choose Custom path for nested values like <code>current_agreement.start_date</code>.
                            </Alert>
                            <FormGroup className="add-custom-column-form-group">
                                <Label for="parentColumnId">Parent FK Column</Label>
                                <Select
                                    options={(boardColumnsData?.allColumns?.edges || [])
                                        .filter((column) => column.node.columnType === "FK_OBJECT")
                                        .map((column) => ({ value: column.node.id, label: column.node.title }))}
                                    name="parentColumnId"
                                    id="parentColumnId"
                                    value={(boardColumnsData?.allColumns?.edges || [])
                                        .filter((column) => column.node.id === formData.parentColumnId)
                                        .map((column) => ({ value: column.node.id, label: column.node.title }))[0] || null}
                                    onChange={(value) => handleSelectChange("parentColumnId", value?.value || null)}
                                    {...selectMenuProps}
                                />
                            </FormGroup>
                            <FormGroup className="add-custom-column-form-group">
                                <Label for="mirrorField">Mirror Value</Label>
                                <Select
                                    options={mirrorFieldOptions}
                                    name="mirrorField"
                                    id="mirrorField"
                                    value={formData.mirrorField ? { value: formData.mirrorField, label: mirrorFieldLabel } : null}
                                    onChange={(value) => handleSelectChange("mirrorField", value?.value || "")}
                                    isDisabled={!formData.parentColumnId}
                                    {...selectMenuProps}
                                />
                            </FormGroup>
                            {formData.mirrorField === CUSTOM_MIRROR_OPTION && (
                                <FormGroup className="add-custom-column-form-group">
                                    <Label for="customMirrorPath">Custom Path</Label>
                                    <div className="input-group">
                                        <div className="input-group-prepend">
                                            <span className="input-group-text">{selectedParentColumn?.contentType?.model || ""}.</span>
                                        </div>
                                        <Input
                                            type="text"
                                            name="customMirrorPath"
                                            id="customMirrorPath"
                                            value={formData.customMirrorPath}
                                            onChange={(e) => handleSelectChange("customMirrorPath", e.target.value)}
                                            placeholder="current_agreement.start_date"
                                            disabled={!formData.parentColumnId}
                                        />
                                    </div>
                                </FormGroup>
                            )}
                        </>}
                        {"ENUM" === formData.columnType && (
                            <FormGroup className="add-custom-column-form-group">
                                <Label>Options</Label>
                                {(formData.allowedOptions || []).map((opt, index) => (
                                    <div key={index} className="input-group mb-1">
                                        <Input
                                            type="text"
                                            value={opt}
                                            onChange={(e) => handleEnumOptionChange(index, e.target.value)}
                                            placeholder={`Option ${index + 1}`}
                                        />
                                        <Button color="danger" size="sm" onClick={() => handleRemoveEnumOption(index)}>×</Button>
                                    </div>
                                ))}
                                <Button color="secondary" size="sm" type="button" onClick={handleAddEnumOption} style={{ width: '100%' }}>+ Add Option</Button>
                            </FormGroup>
                        )}
                        <FormGroup check className="add-custom-column-required">
                            <Input type="checkbox" name="isRequired" id="isRequired" checked={formData.isRequired} onChange={handleChange} />
                            <Label for="isRequired">Require this field on tasks</Label>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter className="add-custom-column-modal-footer">
                    <Button color="secondary" type="button" onClick={() => setOpenModal(false)} disabled={loading}>Cancel</Button>
                    <Button color="primary" type="submit" form="addCustomColumnForm" disabled={loading}>
                        {loading ? "Creating..." : "Create Column"}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
}

export default RenderAddCustomColumn
